import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { parseM3U, parseSingleStream } from '../utils/m3uParser';
const MOCK_QUEUE = [
    { id: '1', type: 'youtube', title: 'Music Video', duration: 3600, isCurrent: false },
    { id: '2', type: 'youtube', title: 'Tutorial', duration: 2700, isCurrent: true },
];
// Color palette for calendar events
const COLOR_PALETTE = [
    '#ef4444', // Red
    '#f97316', // Orange
    '#f59e0b', // Amber
    '#10b981', // Emerald
    '#06b6d4', // Cyan
    '#3b82f6', // Blue
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#6366f1', // Indigo
    '#14b8a6', // Teal
    '#84cc16', // Lime
    '#f43f5e', // Rose
];
const CalendarAdminPanel = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [scheduledEvents, setScheduledEvents] = useState([]);
    const [broadcastMode, setBroadcastMode] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedEvent, setDraggedEvent] = useState(null);
    const [hoveredSlot, setHoveredSlot] = useState(null);
    const [activeTab, setActiveTab] = useState('media');
    const [mediaSubTab, setMediaSubTab] = useState('youtube'); // Sub-tab within Media: youtube, server, streams
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [youtubeItems, setYoutubeItems] = useState([]);
    const [isLoadingYouTube, setIsLoadingYouTube] = useState(false);
    const [expandedSlot, setExpandedSlot] = useState(null);
    const [hoveredEvent, setHoveredEvent] = useState(null);
    // Socket state
    const [socket, setSocket] = useState(null);
    const [calendarChannel, setCalendarChannel] = useState(null);
    // Server files state
    const [serverFiles, setServerFiles] = useState([]);
    const [isLoadingServerFiles, setIsLoadingServerFiles] = useState(false);
    // Stream state
    const [streamItems, setStreamItems] = useState([]);
    const [streamUrl, setStreamUrl] = useState('');
    const [streamTitle, setStreamTitle] = useState('');
    // Broadcast sources state
    const [broadcastSources, setBroadcastSources] = useState([]);
    const [currentBroadcastIndex, setCurrentBroadcastIndex] = useState(0);
    const [broadcastYoutubeUrl, setBroadcastYoutubeUrl] = useState('');
    const [broadcastPlaylistUrl, setBroadcastPlaylistUrl] = useState('');
    const [playlistMode, setPlaylistMode] = useState('individual');
    const [expandedBroadcastSources, setExpandedBroadcastSources] = useState(new Set());
    // Smart shift visual feedback
    const [ghostPreview, setGhostPreview] = useState(null);
    const [shiftIndicators, setShiftIndicators] = useState([]);
    // Initialize socket connection
    useEffect(() => {
        // Get auth token from localStorage
        const token = localStorage.getItem('authToken');
        const newSocket = io({
            transports: ['websocket', 'polling'],
            auth: {
                token: token || undefined
            }
        });
        newSocket.on('connect', () => {
            console.log('ðŸ“¡ Calendar socket connected:', newSocket.id);
            // Get roomId from URL query parameter
            const urlParams = new URLSearchParams(window.location.search);
            const roomId = urlParams.get('roomId') || 'default';
            // Join the room to receive broadcasts (use authenticated username, not 'Calendar')
            console.log('ðŸ“¡ Calendar joining room:', roomId);
            newSocket.emit('join-room', { roomId, username: token ? undefined : 'Calendar' }, (response) => {
                console.log('ðŸ“¡ Calendar join-room response:', response);
                // Only request data after successfully joining the room
                if (response && response.success) {
                    console.log('ðŸ“¡ Calendar successfully joined room, requesting data...');
                    newSocket.emit('get-server-files');
                    newSocket.emit('get-scheduled-events');
                    newSocket.emit('get-broadcast-sources');
                    newSocket.emit('get-broadcast-status');
                    setIsLoadingServerFiles(true);
                }
                else {
                    console.error('âŒ Calendar failed to join room:', response);
                    setIsLoadingServerFiles(false);
                }
            });
        });
        newSocket.on('disconnect', () => {
            console.log('ðŸ“¡ Calendar socket disconnected');
        });
        // Listen for server files updates
        newSocket.on('server-files-updated', (data) => {
            console.log('ðŸ“ Received server files:', data.files);
            setIsLoadingServerFiles(false);
            if (data.files && data.files.length > 0) {
                const formattedFiles = data.files.map(file => ({
                    id: file.filename,
                    type: 'media',
                    title: file.title || file.filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '),
                    duration: file.duration || 0,
                    filename: file.filename
                }));
                setServerFiles(formattedFiles);
            }
            else {
                setServerFiles([]);
            }
        });
        newSocket.on('server-files-error', (data) => {
            console.error('âŒ Server files error:', data.message);
            setIsLoadingServerFiles(false);
            setServerFiles([]);
        });
        // Listen for scheduled events updates from server
        newSocket.on('scheduled-events-updated', (data) => {
            console.log('ðŸ“… Received scheduled events from server:', data.scheduledEvents);
            if (data.scheduledEvents && Array.isArray(data.scheduledEvents)) {
                // Convert server format to client format
                const formattedEvents = data.scheduledEvents.map(event => ({
                    id: event.id,
                    type: event.type,
                    startTime: event.scheduledTime,
                    duration: event.data?.duration || 0,
                    title: event.data?.title || 'Untitled',
                    videoId: event.data?.videoId,
                    filename: event.data?.filename,
                    streamUrl: event.data?.streamUrl, // For stream events
                    executed: event.executed
                }));
                // Replace scheduled events while keeping queue events
                setScheduledEvents(prev => {
                    const queueEvents = prev.filter(evt => evt.isQueue);
                    return [...queueEvents, ...formattedEvents];
                });
            }
        });
        // Real-time calendar population: Listen for queue updates
        newSocket.on('queue-data-updated', (data) => {
            console.log('ðŸ“‹ Queue updated, populating calendar with queue:', data.queueData);
            console.log('ðŸ“‹ Queue data length:', data.queueData?.length);
            console.log('ðŸ“‹ First item:', data.queueData?.[0]);
            if (data.queueData && data.queueData.length > 0) {
                // Convert queue to calendar events
                let currentTime = new Date();
                const queueEvents = data.queueData.map((item, index) => {
                    const event = {
                        id: `queue-${item.type}-${item.videoId || item.filename || index}`,
                        type: item.type,
                        startTime: new Date(currentTime).toISOString(),
                        duration: item.duration || 0,
                        title: item.title,
                        videoId: item.videoId,
                        filename: item.filename,
                        executed: false,
                        isQueue: true,
                        isPlaying: index === 0
                    };
                    // Add duration to current time for next item
                    currentTime = new Date(currentTime.getTime() + (item.duration || 180) * 1000);
                    return event;
                });
                // Replace queue events while keeping scheduled events
                setScheduledEvents(prev => {
                    const nonQueueEvents = prev.filter(evt => !evt.isQueue);
                    return [...nonQueueEvents, ...queueEvents];
                });
            }
            else {
                // Queue empty, remove queue events
                setScheduledEvents(prev => prev.filter(evt => !evt.isQueue));
            }
        });
        // Listen for broadcast mode updates from server
        newSocket.on('broadcast-mode-updated', (data) => {
            console.log('ðŸ“º Broadcast mode updated from server:', data.broadcastMode);
            setBroadcastMode(data.broadcastMode);
        });
        // Listen for broadcast sources updates from server
        newSocket.on('broadcast-sources-updated', (data) => {
            console.log('ðŸ“º Broadcast sources updated from server:', data.sources, 'currentIndex:', data.currentIndex);
            setBroadcastSources(data.sources || []);
            setCurrentBroadcastIndex(data.currentIndex || 0);
        });
        // Listen for broadcast errors
        newSocket.on('broadcast-error', (data) => {
            console.error('âŒ Broadcast error:', data.error, data.details);
            alert(`Error: ${data.error}${data.details ? '\n' + data.details : ''}`);
        });
        setSocket(newSocket);
        return () => {
            newSocket.close();
        };
    }, []);
    // Initialize BroadcastChannel for communication with main app
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const roomId = urlParams.get('roomId') || 'default';
        const channel = new BroadcastChannel(`gridchat-calendar-${roomId}`);
        console.log('ðŸ“¡ Calendar BroadcastChannel initialized:', `gridchat-calendar-${roomId}`);
        setCalendarChannel(channel);
        return () => {
            channel.close();
        };
    }, []);
    // Timer to check for scheduled events that should start
    useEffect(() => {
        const checkScheduledEvents = () => {
            if (!socket)
                return;
            const now = new Date();
            scheduledEvents.forEach(event => {
                // Skip if already executed
                if (event.executed)
                    return;
                const eventTime = new Date(event.startTime);
                const timeDiff = eventTime.getTime() - now.getTime();
                // Trigger if event should have started (within last 10 seconds to account for delays)
                if (timeDiff <= 0 && timeDiff > -10000) {
                    console.log('â° Triggering scheduled event:', event);
                    // Emit socket event to trigger playback
                    if (event.type === 'youtube') {
                        socket.emit('calendar-trigger-youtube', {
                            videoId: event.videoId,
                            title: event.title,
                            eventId: event.id
                        });
                    }
                    else if (event.type === 'media') {
                        socket.emit('calendar-trigger-media', {
                            filename: event.filename,
                            title: event.title,
                            eventId: event.id
                        });
                    }
                    else if (event.type === 'stream') {
                        socket.emit('calendar-trigger-stream', {
                            streamUrl: event.streamUrl,
                            title: event.title,
                            eventId: event.id
                        });
                    }
                    // Mark as executed locally
                    setScheduledEvents(prev => prev.map(evt => evt.id === event.id ? { ...evt, executed: true } : evt));
                }
            });
        };
        // Check every second
        const interval = setInterval(checkScheduledEvents, 1000);
        return () => clearInterval(interval);
    }, [socket, scheduledEvents]);
    // Fetch server media files via socket
    const fetchServerFiles = () => {
        if (socket) {
            console.log('ðŸ“ Requesting server files via socket...');
            setIsLoadingServerFiles(true);
            socket.emit('get-server-files');
        }
    };
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++)
            days.push(null);
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }
        return days;
    };
    const days = getDaysInMonth(currentMonth);
    const monthYearText = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const previousMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    // ==========================================
    // COLOR SYSTEM
    // ==========================================
    const getEventColor = (event, allEvents) => {
        // Broadcast always gets yellow
        if (event.type === 'broadcast') {
            return '#eab308';
        }
        // Sort all events by startTime (chronological order)
        const sortedEvents = allEvents
            .filter(e => e.type !== 'broadcast')
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        // Find this event's position in chronological order
        const index = sortedEvents.findIndex(e => e.id === event.id);
        // Assign color based on position (wraps around if more than 12 events)
        const colorIndex = index % COLOR_PALETTE.length;
        return COLOR_PALETTE[colorIndex];
    };
    // ==========================================
    // SMART SHIFT ALGORITHM - Helper Functions
    // ==========================================
    const calculateGapBefore = (dropTime, existingEvents) => {
        const dropDate = new Date(dropTime);
        const eventsBefore = existingEvents
            .map(event => {
            const eventStart = new Date(event.startTime);
            const eventEnd = new Date(eventStart.getTime() + event.duration * 1000);
            return { event, eventEnd };
        })
            .filter(({ eventEnd }) => eventEnd <= dropDate)
            .sort((a, b) => b.eventEnd - a.eventEnd);
        if (eventsBefore.length === 0) {
            const startOfHour = new Date(dropDate);
            startOfHour.setMinutes(0, 0, 0);
            return (dropDate - startOfHour) / 1000;
        }
        const lastEventEnd = eventsBefore[0].eventEnd;
        return Math.max(0, (dropDate - lastEventEnd) / 1000);
    };
    const calculateGapAfter = (newVideoEnd, existingEvents) => {
        const endDate = new Date(newVideoEnd);
        const eventsAfter = existingEvents
            .map(event => {
            const eventStart = new Date(event.startTime);
            return { event, eventStart };
        })
            .filter(({ eventStart }) => eventStart >= endDate)
            .sort((a, b) => a.eventStart - b.eventStart);
        if (eventsAfter.length === 0) {
            return 24 * 3600;
        }
        const nextEventStart = eventsAfter[0].eventStart;
        return Math.max(0, (nextEventStart - endDate) / 1000);
    };
    const findConflicts = (newStart, newEnd, existingEvents) => {
        return existingEvents.filter(event => {
            const eventStart = new Date(event.startTime);
            const eventEnd = new Date(eventStart.getTime() + event.duration * 1000);
            return newStart < eventEnd && newEnd > eventStart;
        });
    };
    const shiftVideoBackward = (newVideo, requestedStart, gapBefore) => {
        const shiftAmount = Math.min(gapBefore, newVideo.duration) * 1000;
        const finalStart = new Date(requestedStart.getTime() - shiftAmount);
        return {
            finalStartTime: finalStart.toISOString(),
            eventsToShift: [],
            message: `Placed ${(shiftAmount / 60000).toFixed(0)} minutes earlier to use available gap`
        };
    };
    const shiftBidirectional = (newVideo, requestedStart, gapBefore, gapAfter, conflicts) => {
        const neededSpace = newVideo.duration;
        const totalGap = gapBefore + gapAfter;
        const additionalSpaceNeeded = neededSpace - totalGap;
        if (additionalSpaceNeeded <= 0) {
            const useGapBefore = Math.min(gapBefore, neededSpace / 2);
            const finalStart = new Date(requestedStart.getTime() - useGapBefore * 1000);
            return {
                finalStartTime: finalStart.toISOString(),
                eventsToShift: [],
                message: 'Optimally placed using available gaps'
            };
        }
        const shiftBackward = gapBefore * 1000;
        const finalStart = new Date(requestedStart.getTime() - shiftBackward);
        const finalEnd = new Date(finalStart.getTime() + neededSpace * 1000);
        const eventsToShift = conflicts.map(event => {
            const eventStart = new Date(event.startTime);
            const newEventStart = new Date(Math.max(eventStart.getTime(), finalEnd.getTime()));
            const shiftAmount = newEventStart.getTime() - eventStart.getTime();
            return {
                eventId: event.id,
                newStartTime: newEventStart.toISOString(),
                shiftAmount: shiftAmount,
                shiftMinutes: Math.round(shiftAmount / 60000)
            };
        });
        return {
            finalStartTime: finalStart.toISOString(),
            eventsToShift: eventsToShift,
            message: `${eventsToShift.length} video(s) shifted to make room`
        };
    };
    const shiftEverythingRight = (newVideo, requestedStart, conflicts) => {
        const newEnd = new Date(requestedStart.getTime() + newVideo.duration * 1000);
        const sortedConflicts = [...conflicts].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        const eventsToShift = [];
        let currentShiftEnd = newEnd;
        for (const event of sortedConflicts) {
            const eventStart = new Date(event.startTime);
            const eventDuration = event.duration * 1000;
            const newEventStart = new Date(Math.max(eventStart.getTime(), currentShiftEnd.getTime()));
            const shiftAmount = newEventStart.getTime() - eventStart.getTime();
            if (shiftAmount > 0) {
                eventsToShift.push({
                    eventId: event.id,
                    newStartTime: newEventStart.toISOString(),
                    shiftAmount: shiftAmount,
                    shiftMinutes: Math.round(shiftAmount / 60000)
                });
                currentShiftEnd = new Date(newEventStart.getTime() + eventDuration);
            }
        }
        return {
            finalStartTime: requestedStart.toISOString(),
            eventsToShift: eventsToShift,
            message: `${eventsToShift.length} video(s) shifted right`
        };
    };
    const smartPlaceVideo = (newVideo, dropTime, existingEvents) => {
        const requestedStart = new Date(dropTime);
        const requestedEnd = new Date(requestedStart.getTime() + newVideo.duration * 1000);
        console.log('Smart Place Video:', {
            title: newVideo.title,
            requestedStart: requestedStart.toLocaleString(),
            duration: newVideo.duration / 60 + ' minutes'
        });
        const conflicts = findConflicts(requestedStart, requestedEnd, existingEvents);
        if (conflicts.length === 0) {
            return {
                finalStartTime: dropTime,
                eventsToShift: [],
                message: null
            };
        }
        console.log(`Found ${conflicts.length} conflicts`);
        const gapBefore = calculateGapBefore(dropTime, existingEvents);
        const gapAfter = calculateGapAfter(requestedEnd, existingEvents);
        console.log('Gaps:', {
            before: gapBefore / 60 + ' min',
            after: gapAfter / 60 + ' min'
        });
        if (gapBefore >= newVideo.duration) {
            return shiftVideoBackward(newVideo, requestedStart, gapBefore);
        }
        else if (gapBefore > 0 && gapAfter > 0) {
            return shiftBidirectional(newVideo, requestedStart, gapBefore, gapAfter, conflicts);
        }
        else {
            return shiftEverythingRight(newVideo, requestedStart, conflicts);
        }
    };
    const showToast = (message) => {
        console.log('ðŸ“¢', message);
    };
    // ==========================================
    // EXISTING HELPER FUNCTIONS
    // ==========================================
    const getEventHourSpan = (event) => {
        const start = new Date(event.startTime);
        const end = new Date(start.getTime() + event.duration * 1000);
        const hours = [];
        let currentHour = new Date(start);
        currentHour.setMinutes(0, 0, 0);
        while (currentHour < end) {
            hours.push({
                date: currentHour.toDateString(),
                hour: currentHour.getHours(),
                isStart: hours.length === 0
            });
            currentHour = new Date(currentHour.getTime() + 3600000);
        }
        return hours;
    };
    const getHourFillPercentage = (date, hour, event) => {
        const hourStart = new Date(date);
        hourStart.setHours(hour, 0, 0, 0);
        const hourEnd = new Date(hourStart);
        hourEnd.setHours(hour + 1, 0, 0, 0);
        const eventStart = new Date(event.startTime);
        const eventEnd = new Date(eventStart.getTime() + event.duration * 1000);
        const overlapStart = eventStart < hourStart ? hourStart : eventStart;
        const overlapEnd = eventEnd > hourEnd ? hourEnd : eventEnd;
        if (overlapStart >= overlapEnd)
            return 0;
        const overlapMs = overlapEnd - overlapStart;
        const hourMs = 3600000;
        return (overlapMs / hourMs) * 100;
    };
    const getScheduledContentForHour = (date, hour) => {
        if (!date)
            return [];
        return scheduledEvents.filter(event => {
            const hourSpan = getEventHourSpan(event);
            return hourSpan.some(h => h.date === date.toDateString() && h.hour === hour);
        });
    };
    const getBroadcastFillPercentage = (date, hour, eventsInHour) => {
        if (broadcastMode !== true)
            return 0;
        const usedPercentage = eventsInHour.reduce((total, event) => {
            return total + getHourFillPercentage(date, hour, event);
        }, 0);
        return Math.max(0, 100 - usedPercentage);
    };
    // ==========================================
    // BROADCAST MODE HANDLER
    // ==========================================
    const handleBroadcastModeToggle = () => {
        const newMode = !broadcastMode;
        console.log(`ðŸ“º Toggling broadcast mode to: ${newMode}`);
        // Update local state immediately for UI responsiveness
        setBroadcastMode(newMode);
        // Emit socket event directly
        if (socket) {
            socket.emit('update-broadcast-mode', { enabled: newMode });
            console.log(`ðŸ“º Emitted update-broadcast-mode to server: ${newMode}`);
        }
        // Also post to BroadcastChannel for main app (if available)
        try {
            calendarChannel?.postMessage({
                type: 'update-broadcast-mode',
                data: { enabled: newMode }
            });
        }
        catch (err) {
            console.warn('âš ï¸ BroadcastChannel post failed (channel may be closed):', err);
        }
    };
    // ==========================================
    // BROADCAST SOURCES HANDLERS
    // ==========================================
    const extractYouTubeVideoId = (input) => {
        // Handle full URLs: youtube.com/watch?v=VIDEO_ID
        let match = input.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
        if (match)
            return match[1];
        // Handle embed URLs: youtube.com/embed/VIDEO_ID
        match = input.match(/youtube\.com\/embed\/([^&\s?]+)/);
        if (match)
            return match[1];
        // Handle direct ID (11 characters)
        if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) {
            return input.trim();
        }
        return null;
    };
    const extractYouTubePlaylistId = (input) => {
        // Handle URLs with list parameter
        const match = input.match(/[?&]list=([^&\s]+)/);
        if (match)
            return match[1];
        // Handle direct ID (starts with PL, UU, FL, RD)
        if (/^(PL|UU|FL|RD)[a-zA-Z0-9_-]+$/.test(input.trim())) {
            return input.trim();
        }
        return null;
    };
    const handleAddBroadcastYouTubeVideo = () => {
        const videoId = extractYouTubeVideoId(broadcastYoutubeUrl);
        if (!videoId) {
            alert('Please enter a valid YouTube video URL or ID');
            return;
        }
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        console.log('ðŸ“º Adding YouTube video to broadcast sources:', videoId);
        socket.emit('add-broadcast-source', {
            type: 'youtube',
            videoId: videoId
        });
        setBroadcastYoutubeUrl('');
    };
    const handleAddBroadcastYouTubePlaylist = () => {
        const playlistId = extractYouTubePlaylistId(broadcastPlaylistUrl);
        if (!playlistId) {
            alert('Please enter a valid YouTube playlist URL or ID');
            return;
        }
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        console.log('ðŸ“º Adding YouTube playlist to broadcast sources:', playlistId, playlistMode);
        socket.emit('add-broadcast-source', {
            type: 'youtube-playlist',
            playlistId: playlistId,
            mode: playlistMode
        });
        setBroadcastPlaylistUrl('');
    };
    const handleRemoveBroadcastSource = (sourceId) => {
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        // Don't allow removing the local files source
        if (sourceId === 'local-files') {
            alert('Cannot remove local files source');
            return;
        }
        console.log('ðŸ“º Removing broadcast source:', sourceId);
        socket.emit('remove-broadcast-source', { sourceId });
    };
    const handleToggleBroadcastSource = (sourceId) => {
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        console.log('ðŸ“º Toggling broadcast source:', sourceId);
        socket.emit('toggle-broadcast-source', { sourceId });
    };
    const handleRefreshPlaylistSource = (sourceId) => {
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        console.log('ðŸ“º Refreshing playlist source:', sourceId);
        socket.emit('refresh-playlist-source', { sourceId });
    };
    const handleRemoveVideoFromPlaylist = (sourceId, videoId) => {
        if (!socket) {
            console.error('Socket not connected');
            return;
        }
        console.log('ðŸ“º Removing video from playlist:', sourceId, videoId);
        socket.emit('remove-video-from-playlist', { sourceId, videoId });
    };
    const toggleBroadcastSourceExpanded = (sourceId) => {
        setExpandedBroadcastSources(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sourceId)) {
                newSet.delete(sourceId);
            }
            else {
                newSet.add(sourceId);
            }
            return newSet;
        });
    };
    // ==========================================
    // DRAG AND DROP HANDLERS
    // ==========================================
    const handleDragStart = (e, item) => {
        setDraggedItem(item);
        e.dataTransfer.effectAllowed = 'copy';
        const dragPreview = document.createElement('div');
        dragPreview.style.cssText = `
      position: absolute;
      top: -1000px;
      padding: 6px 10px;
      background: rgba(0,0,0,0.9);
      border: 2px solid ${item.type === 'youtube' ? '#ef4444' : '#3b82f6'};
      border-radius: 6px;
      color: white;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
        dragPreview.textContent = `${item.type === 'youtube' ? 'ðŸ”´' : 'ðŸ”µ'} ${item.title}`;
        document.body.appendChild(dragPreview);
        e.dataTransfer.setDragImage(dragPreview, 0, 0);
        setTimeout(() => document.body.removeChild(dragPreview), 0);
    };
    const handleEventDragStart = (e, event) => {
        e.stopPropagation();
        setDraggedEvent(event);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e, date, hour) => {
        e.preventDefault();
        if (!date) {
            setGhostPreview(null);
            setShiftIndicators([]);
            return;
        }
        const dropTime = new Date(date);
        dropTime.setHours(hour, 0, 0, 0);
        const itemToDrop = draggedEvent || draggedItem;
        if (!itemToDrop)
            return;
        e.dataTransfer.dropEffect = draggedEvent ? 'move' : 'copy';
        const eventsToConsider = draggedEvent
            ? scheduledEvents.filter(evt => evt.id !== draggedEvent.id)
            : scheduledEvents;
        const result = smartPlaceVideo(itemToDrop, dropTime.toISOString(), eventsToConsider);
        setGhostPreview({
            startTime: result.finalStartTime,
            duration: itemToDrop.duration,
            title: itemToDrop.title,
            type: itemToDrop.type
        });
        setShiftIndicators(result.eventsToShift.map(shift => ({
            eventId: shift.eventId,
            shiftMinutes: shift.shiftMinutes,
            direction: 'right'
        })));
    };
    const handleDrop = (e, date, hour) => {
        e.preventDefault();
        if (!date)
            return;
        const dropTime = new Date(date);
        dropTime.setHours(hour, 0, 0, 0);
        const itemToDrop = draggedEvent || draggedItem;
        if (!itemToDrop)
            return;
        const eventsToConsider = draggedEvent
            ? scheduledEvents.filter(evt => evt.id !== draggedEvent.id)
            : scheduledEvents;
        const result = smartPlaceVideo(itemToDrop, dropTime.toISOString(), eventsToConsider);
        let updatedEvents = [...eventsToConsider];
        result.eventsToShift.forEach(shift => {
            const eventIndex = updatedEvents.findIndex(e => e.id === shift.eventId);
            if (eventIndex !== -1) {
                updatedEvents[eventIndex] = {
                    ...updatedEvents[eventIndex],
                    startTime: shift.newStartTime
                };
            }
        });
        // If moving an existing event, post to BroadcastChannel
        if (draggedEvent) {
            if (calendarChannel) {
                calendarChannel.postMessage({
                    type: 'move-scheduled-event',
                    data: {
                        eventId: draggedEvent.id,
                        newScheduledTime: result.finalStartTime
                    }
                });
                console.log('ðŸ“ Moving scheduled event:', draggedEvent.id, 'to', result.finalStartTime);
            }
        }
        else {
            // Adding a new event, post to BroadcastChannel
            const newEvent = {
                id: `event-${Date.now()}`,
                type: draggedItem.type,
                scheduledTime: result.finalStartTime,
                data: {
                    videoId: draggedItem.videoId,
                    title: draggedItem.title,
                    duration: draggedItem.duration,
                    filename: draggedItem.filename,
                    thumbnail: draggedItem.thumbnail,
                    path: draggedItem.path || `/media/${draggedItem.filename}`,
                    streamUrl: draggedItem.streamUrl // For stream events
                }
            };
            if (calendarChannel) {
                calendarChannel.postMessage({
                    type: 'schedule-event',
                    data: { event: newEvent }
                });
                console.log('ðŸ“… Scheduling new event:', newEvent);
            }
        }
        setDraggedEvent(null);
        setDraggedItem(null);
        setGhostPreview(null);
        setShiftIndicators([]);
        setHoveredSlot(null);
        if (result.message) {
            showToast(result.message);
        }
    };
    const handleRemoveEvent = (eventId, e) => {
        e.stopPropagation();
        if (calendarChannel) {
            calendarChannel.postMessage({
                type: 'remove-scheduled-event',
                data: { eventId }
            });
            console.log('ðŸ—‘ï¸ Removing scheduled event:', eventId);
        }
    };
    const handleSlotClick = (date, hour, e) => {
        if (e.target.closest('[draggable="true"]'))
            return;
        const slotKey = `${date.toDateString()}-${hour}`;
        if (expandedSlot?.key === slotKey) {
            setExpandedSlot(null);
        }
        else {
            setExpandedSlot({ key: slotKey, date, hour });
        }
    };
    // ==========================================
    // YOUTUBE FUNCTIONS
    // ==========================================
    const extractVideoId = (url) => {
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };
    const handleAddYouTube = async () => {
        if (!youtubeUrl || isLoadingYouTube)
            return;
        const videoId = extractVideoId(youtubeUrl);
        if (!videoId) {
            alert('Invalid YouTube URL');
            return;
        }
        setIsLoadingYouTube(true);
        try {
            const response = await fetch(`/api/youtube-info?videoId=${videoId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch video info');
            }
            const videoInfo = await response.json();
            const newVideo = {
                id: `yt-${Date.now()}`,
                type: 'youtube',
                title: videoInfo.title,
                duration: videoInfo.duration,
                videoId: videoId
            };
            setYoutubeItems([...youtubeItems, newVideo]);
            setYoutubeUrl('');
        }
        catch (error) {
            console.error('Error fetching YouTube info:', error);
            alert('Failed to fetch video information');
        }
        finally {
            setIsLoadingYouTube(false);
        }
    };
    // Handle M3U file upload
    const handleM3UFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        try {
            const text = await file.text();
            const parsed = parseM3U(text);
            if (parsed.length === 0) {
                alert('No streams found in M3U file');
                return;
            }
            const newStreams = parsed.map((stream, index) => ({
                id: `stream-${Date.now()}-${index}`,
                type: 'stream',
                title: stream.title,
                duration: stream.duration || 3600, // Default 1 hour if not specified
                streamUrl: stream.url
            }));
            setStreamItems([...streamItems, ...newStreams]);
            console.log(`ðŸ“º Imported ${newStreams.length} streams from M3U file`);
            console.log('ðŸ“º First 3 stream titles:', newStreams.slice(0, 3).map(s => s.title));
            // Reset the file input
            e.target.value = '';
        }
        catch (error) {
            console.error('Error parsing M3U file:', error);
            alert('Failed to parse M3U file');
        }
    };
    // Handle adding single stream URL
    const handleAddStreamUrl = () => {
        if (!streamUrl.trim())
            return;
        const parsed = parseSingleStream(streamUrl, streamTitle || undefined);
        if (!parsed) {
            alert('Invalid stream URL');
            return;
        }
        const newStream = {
            id: `stream-${Date.now()}`,
            type: 'stream',
            title: parsed.title,
            duration: 3600, // Default 1 hour for streams
            streamUrl: parsed.url
        };
        setStreamItems([...streamItems, newStream]);
        setStreamUrl('');
        setStreamTitle('');
        console.log('ðŸ“º Added stream:', newStream.title);
    };
    // Handle removing stream from list
    const handleRemoveStream = (streamId) => {
        setStreamItems(streamItems.filter(s => s.id !== streamId));
    };
    // ==========================================
    // UTILITY FUNCTIONS
    // ==========================================
    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };
    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        if (hours > 0)
            return `${hours}h ${mins}m`;
        return `${mins}m`;
    };
    const formatSize = (bytes) => {
        if (!bytes)
            return '';
        const mb = bytes / (1024 * 1024);
        if (mb < 1024)
            return `${mb.toFixed(1)} MB`;
        return `${(mb / 1024).toFixed(1)} GB`;
    };
    const tabStyle = (isActive) => ({
        padding: '8px 12px',
        background: isActive ? 'rgba(139, 92, 246, 0.3)' : 'transparent',
        border: 'none',
        borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
        color: 'white',
        cursor: 'pointer',
        fontSize: '11px',
        fontWeight: '600',
        flex: 1
    });
    // ==========================================
    // RENDER HELPERS
    // ==========================================
    // Handle minute-level drag and drop in expanded hour view
    const handleExpandedHourDrop = (e, date, hour) => {
        e.preventDefault();
        e.stopPropagation();
        if (!date)
            return;
        const itemToDrop = draggedEvent || draggedItem;
        if (!itemToDrop)
            return;
        // Calculate the exact minute based on drop position
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const minute = Math.floor((x / rect.width) * 60);
        const clampedMinute = Math.max(0, Math.min(59, minute));
        const dropTime = new Date(date);
        dropTime.setHours(hour, clampedMinute, 0, 0);
        console.log(`ðŸ“ Minute-level drop at ${hour}:${clampedMinute.toString().padStart(2, '0')}`);
        const eventsToConsider = draggedEvent
            ? scheduledEvents.filter(evt => evt.id !== draggedEvent.id)
            : scheduledEvents;
        const result = smartPlaceVideo(itemToDrop, dropTime.toISOString(), eventsToConsider);
        let updatedEvents = [...eventsToConsider];
        result.eventsToShift.forEach(shift => {
            const eventIndex = updatedEvents.findIndex(e => e.id === shift.eventId);
            if (eventIndex !== -1) {
                updatedEvents[eventIndex] = {
                    ...updatedEvents[eventIndex],
                    startTime: shift.newStartTime
                };
            }
        });
        // If moving an existing event, post to BroadcastChannel
        if (draggedEvent) {
            if (calendarChannel) {
                calendarChannel.postMessage({
                    type: 'move-scheduled-event',
                    data: {
                        eventId: draggedEvent.id,
                        newScheduledTime: result.finalStartTime
                    }
                });
                console.log('ðŸ“ Moving scheduled event:', draggedEvent.id, 'to', result.finalStartTime);
            }
        }
        else {
            // Adding a new event, post to BroadcastChannel
            const newEvent = {
                id: `event-${Date.now()}`,
                type: draggedItem.type,
                scheduledTime: result.finalStartTime,
                data: {
                    videoId: draggedItem.videoId,
                    title: draggedItem.title,
                    duration: draggedItem.duration,
                    filename: draggedItem.filename,
                    thumbnail: draggedItem.thumbnail,
                    path: draggedItem.path || `/media/${draggedItem.filename}`,
                    streamUrl: draggedItem.streamUrl // For stream events
                }
            };
            if (calendarChannel) {
                calendarChannel.postMessage({
                    type: 'schedule-event',
                    data: { event: newEvent }
                });
                console.log('ðŸ“… Scheduling new event:', newEvent);
            }
        }
        setDraggedEvent(null);
        setDraggedItem(null);
        setGhostPreview(null);
        setShiftIndicators([]);
        setHoveredSlot(null);
        if (result.message) {
            showToast(result.message);
        }
    };
    const renderExpandedHourView = (date, hour) => {
        const eventsInHour = getScheduledContentForHour(date, hour);
        const hourStart = new Date(date);
        hourStart.setHours(hour, 0, 0, 0);
        return (_jsxs("div", { style: {
                gridColumn: '1 / -1',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '4px',
                padding: '8px',
                border: '2px solid #8b5cf6',
                marginTop: '4px'
            }, children: [_jsxs("div", { style: { fontSize: '11px', fontWeight: '700', marginBottom: '8px', color: '#8b5cf6' }, children: ["Expanded: ", hour === 0 ? '12am' : hour < 12 ? `${hour}am` : hour === 12 ? '12pm' : `${hour - 12}pm`, " (Click again to collapse)"] }), _jsxs("div", { onDragOver: (e) => e.preventDefault(), onDrop: (e) => handleExpandedHourDrop(e, date, hour), style: {
                        display: 'flex',
                        height: '60px',
                        position: 'relative',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        cursor: (draggedItem || draggedEvent) ? 'copy' : 'default'
                    }, children: [Array.from({ length: 60 }, (_, minute) => (_jsx("div", { style: {
                                flex: 1,
                                borderRight: minute % 5 === 4 ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.1)',
                                position: 'relative'
                            }, children: minute % 15 === 0 && (_jsxs("div", { style: {
                                    position: 'absolute',
                                    bottom: '2px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '9px',
                                    color: 'rgba(255,255,255,0.6)'
                                }, children: [":", minute.toString().padStart(2, '0')] })) }, minute))), eventsInHour.map(event => {
                            const eventStart = new Date(event.startTime);
                            const eventEnd = new Date(eventStart.getTime() + event.duration * 1000);
                            const hourEnd = new Date(hourStart.getTime() + 3600000);
                            const startMinute = eventStart >= hourStart
                                ? (eventStart.getTime() - hourStart.getTime()) / 60000
                                : 0;
                            const endMinute = eventEnd <= hourEnd
                                ? (eventEnd.getTime() - hourStart.getTime()) / 60000
                                : 60;
                            const leftPercent = (startMinute / 60) * 100;
                            const widthPercent = ((endMinute - startMinute) / 60) * 100;
                            const eventColor = getEventColor(event, scheduledEvents);
                            return (_jsxs("div", { draggable: true, onDragStart: (e) => handleEventDragStart(e, event), onMouseEnter: () => setHoveredEvent(event.id), onMouseLeave: () => setHoveredEvent(null), style: {
                                    position: 'absolute',
                                    left: `${leftPercent}%`,
                                    width: `${widthPercent}%`,
                                    top: '8px',
                                    height: 'calc(100% - 24px)',
                                    background: eventColor,
                                    opacity: 0.9,
                                    borderRadius: '3px',
                                    cursor: 'move',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: 'white',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    padding: '0 4px',
                                    border: hoveredEvent === event.id ? '2px solid white' : 'none'
                                }, title: event.title, children: [widthPercent > 8 && event.title, _jsx("div", { onClick: (e) => handleRemoveEvent(event.id, e), style: {
                                            position: 'absolute',
                                            top: '2px',
                                            right: '2px',
                                            width: '12px',
                                            height: '12px',
                                            background: 'white',
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '8px',
                                            color: '#ef4444'
                                        }, children: "\u00C3\u2014" })] }, event.id));
                        })] })] }));
    };
    const renderShiftIndicator = (eventId) => {
        const shift = shiftIndicators.find(s => s.eventId === eventId);
        if (!shift)
            return null;
        return (_jsxs("div", { style: {
                position: 'absolute',
                top: '-18px',
                right: '2px',
                background: 'rgba(239, 68, 68, 0.95)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '9px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                zIndex: 5,
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                animation: 'shiftBounce 0.5s ease-in-out infinite'
            }, children: ["\u00E2\u017E\u00A1\u00EF\u00B8\u008F +", shift.shiftMinutes, "min"] }));
    };
    // ==========================================
    // MAIN RENDER
    // ==========================================
    return (_jsxs("div", { style: {
            width: '100%',
            height: '100vh',
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            padding: '16px',
            color: 'white',
            display: 'flex',
            gap: '16px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            overflow: 'hidden'
        }, children: [_jsx("style", { children: `
        @keyframes ghostPulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.02);
          }
        }
        
        @keyframes shiftBounce {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(3px);
          }
        }
      ` }), _jsxs("div", { style: {
                    width: '250px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }, children: [_jsx("div", { style: {
                            padding: '10px',
                            background: 'rgba(234, 179, 8, 0.1)',
                            borderRadius: '8px',
                            border: '1px solid rgba(234, 179, 8, 0.3)'
                        }, children: _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [_jsx("span", { style: { fontSize: '11px', fontWeight: '600' }, children: "Broadcast Mode" }), _jsx("div", { onClick: broadcastMode !== null ? handleBroadcastModeToggle : undefined, style: {
                                        width: '36px',
                                        height: '20px',
                                        background: broadcastMode === true ? '#10b981' : 'rgba(255,255,255,0.2)',
                                        borderRadius: '10px',
                                        position: 'relative',
                                        cursor: broadcastMode !== null ? 'pointer' : 'default',
                                        transition: 'all 0.3s',
                                        opacity: broadcastMode !== null ? 1 : 0.5
                                    }, children: _jsx("div", { style: {
                                            width: '16px',
                                            height: '16px',
                                            background: 'white',
                                            borderRadius: '8px',
                                            position: 'absolute',
                                            top: '2px',
                                            left: broadcastMode === true ? '18px' : '2px',
                                            transition: 'all 0.3s'
                                        } }) })] }) }), _jsxs("div", { style: {
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0
                        }, children: [_jsxs("div", { style: { display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)' }, children: [_jsx("button", { onClick: () => setActiveTab('media'), style: tabStyle(activeTab === 'media'), children: "Media" }), _jsx("button", { onClick: () => setActiveTab('queue'), style: tabStyle(activeTab === 'queue'), children: "Queue" }), _jsx("button", { onClick: () => setActiveTab('broadcast'), style: tabStyle(activeTab === 'broadcast'), children: "Broadcast" })] }), _jsxs("div", { style: { flex: 1, overflowY: 'auto', padding: '12px', minHeight: 0 }, children: [activeTab === 'media' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }, children: [_jsx("button", { onClick: () => setMediaSubTab('youtube'), style: {
                                                            flex: 1,
                                                            padding: '6px 8px',
                                                            background: mediaSubTab === 'youtube' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${mediaSubTab === 'youtube' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                                            borderRadius: '4px',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            cursor: 'pointer',
                                                            fontWeight: mediaSubTab === 'youtube' ? '600' : '400',
                                                            transition: 'all 0.2s'
                                                        }, children: "\u00F0\u0178\u201D\u00B4 YouTube" }), _jsx("button", { onClick: () => setMediaSubTab('server'), style: {
                                                            flex: 1,
                                                            padding: '6px 8px',
                                                            background: mediaSubTab === 'server' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${mediaSubTab === 'server' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                                            borderRadius: '4px',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            cursor: 'pointer',
                                                            fontWeight: mediaSubTab === 'server' ? '600' : '400',
                                                            transition: 'all 0.2s'
                                                        }, children: "\u00F0\u0178\u201D\u00B5 Server" }), _jsx("button", { onClick: () => setMediaSubTab('streams'), style: {
                                                            flex: 1,
                                                            padding: '6px 8px',
                                                            background: mediaSubTab === 'streams' ? 'rgba(6, 182, 212, 0.2)' : 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${mediaSubTab === 'streams' ? 'rgba(6, 182, 212, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                                            borderRadius: '4px',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            cursor: 'pointer',
                                                            fontWeight: mediaSubTab === 'streams' ? '600' : '400',
                                                            transition: 'all 0.2s'
                                                        }, children: "\u00F0\u0178\u201C\u00A1 Streams" })] }), mediaSubTab === 'youtube' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { marginBottom: '12px' }, children: [_jsx("input", { type: "text", placeholder: "Paste YouTube URL", value: youtubeUrl, onChange: (e) => setYoutubeUrl(e.target.value), style: {
                                                                    width: '100%',
                                                                    padding: '8px',
                                                                    background: 'rgba(255,255,255,0.1)',
                                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                                    borderRadius: '4px',
                                                                    color: 'white',
                                                                    fontSize: '11px',
                                                                    marginBottom: '6px'
                                                                } }), _jsx("button", { onClick: handleAddYouTube, disabled: isLoadingYouTube, style: {
                                                                    width: '100%',
                                                                    padding: '8px',
                                                                    background: isLoadingYouTube ? '#666' : '#ef4444',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    color: 'white',
                                                                    fontSize: '11px',
                                                                    cursor: isLoadingYouTube ? 'not-allowed' : 'pointer',
                                                                    fontWeight: '600'
                                                                }, children: isLoadingYouTube ? 'Loading...' : 'Add Video' })] }), _jsx("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: "Drag to Schedule:" }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: youtubeItems.map(item => (_jsxs("div", { draggable: true, onDragStart: (e) => handleDragStart(e, item), style: {
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.05)',
                                                                borderRadius: '6px',
                                                                border: '2px solid #ef4444',
                                                                cursor: 'grab',
                                                                fontSize: '11px'
                                                            }, children: [_jsxs("div", { style: { fontWeight: '600', marginBottom: '2px' }, children: ["\u00F0\u0178\u201D\u00B4 ", item.title] }), _jsx("div", { style: { fontSize: '10px', opacity: 0.6 }, children: formatDuration(item.duration) })] }, item.id))) })] })), mediaSubTab === 'server' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }, children: [_jsx("div", { style: { fontSize: '10px', fontWeight: '600', opacity: 0.7 }, children: "Drag to Schedule:" }), _jsx("button", { onClick: fetchServerFiles, disabled: isLoadingServerFiles, style: {
                                                                    padding: '4px 8px',
                                                                    background: 'rgba(255,255,255,0.1)',
                                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                                    borderRadius: '4px',
                                                                    color: 'white',
                                                                    fontSize: '9px',
                                                                    cursor: isLoadingServerFiles ? 'not-allowed' : 'pointer',
                                                                    fontWeight: '600'
                                                                }, children: isLoadingServerFiles ? 'â†»' : 'ðŸ”„' })] }), isLoadingServerFiles ? (_jsx("div", { style: { textAlign: 'center', padding: '20px', fontSize: '11px', opacity: 0.6 }, children: "Loading videos..." })) : serverFiles.length === 0 ? (_jsxs("div", { style: { textAlign: 'center', padding: '20px', fontSize: '11px', opacity: 0.6 }, children: ["No videos found", _jsxs("div", { style: { fontSize: '9px', marginTop: '8px', opacity: 0.5 }, children: ["Socket: ", socket?.connected ? 'âœ… Connected' : 'âŒ Disconnected'] })] })) : (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: serverFiles.map(item => (_jsxs("div", { draggable: true, onDragStart: (e) => handleDragStart(e, item), style: {
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.05)',
                                                                borderRadius: '6px',
                                                                border: '2px solid #3b82f6',
                                                                cursor: 'grab',
                                                                fontSize: '11px'
                                                            }, children: [_jsxs("div", { style: { fontWeight: '600', marginBottom: '2px' }, children: ["\u00F0\u0178\u201D\u00B5 ", item.title] }), _jsx("div", { style: { fontSize: '10px', opacity: 0.6 }, children: formatDuration(item.duration) })] }, item.id))) }))] })), mediaSubTab === 'streams' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { marginBottom: '12px' }, children: [_jsxs("div", { style: { marginBottom: '12px' }, children: [_jsx("label", { style: {
                                                                            display: 'block',
                                                                            fontSize: '10px',
                                                                            fontWeight: '600',
                                                                            marginBottom: '4px',
                                                                            opacity: 0.7
                                                                        }, children: "Import M3U File:" }), _jsx("input", { type: "file", accept: ".m3u,.m3u8", onChange: handleM3UFileUpload, style: {
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            background: 'rgba(255,255,255,0.1)',
                                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                                            borderRadius: '4px',
                                                                            color: 'white',
                                                                            fontSize: '11px',
                                                                            cursor: 'pointer'
                                                                        } })] }), _jsxs("div", { style: { marginBottom: '8px' }, children: [_jsx("label", { style: {
                                                                            display: 'block',
                                                                            fontSize: '10px',
                                                                            fontWeight: '600',
                                                                            marginBottom: '4px',
                                                                            opacity: 0.7
                                                                        }, children: "Or Add Single Stream:" }), _jsx("input", { type: "text", placeholder: "Stream Title (optional)", value: streamTitle, onChange: (e) => setStreamTitle(e.target.value), style: {
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            background: 'rgba(255,255,255,0.1)',
                                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                                            borderRadius: '4px',
                                                                            color: 'white',
                                                                            fontSize: '11px',
                                                                            marginBottom: '6px'
                                                                        } }), _jsx("input", { type: "text", placeholder: "Stream URL (https://...)", value: streamUrl, onChange: (e) => setStreamUrl(e.target.value), style: {
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            background: 'rgba(255,255,255,0.1)',
                                                                            border: '1px solid rgba(255,255,255,0.2)',
                                                                            borderRadius: '4px',
                                                                            color: 'white',
                                                                            fontSize: '11px',
                                                                            marginBottom: '6px'
                                                                        } }), _jsx("button", { onClick: handleAddStreamUrl, style: {
                                                                            width: '100%',
                                                                            padding: '8px',
                                                                            background: '#06b6d4',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            color: 'white',
                                                                            fontSize: '11px',
                                                                            cursor: 'pointer',
                                                                            fontWeight: '600'
                                                                        }, children: "Add Stream" })] })] }), _jsxs("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: ["Drag to Schedule (", streamItems.length, " streams):"] }), streamItems.length === 0 ? (_jsx("div", { style: { textAlign: 'center', padding: '20px', fontSize: '11px', opacity: 0.6 }, children: "No streams imported yet" })) : (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: streamItems.map(item => (_jsxs("div", { draggable: true, onDragStart: (e) => handleDragStart(e, item), style: {
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.05)',
                                                                borderRadius: '6px',
                                                                border: '2px solid #06b6d4',
                                                                cursor: 'grab',
                                                                fontSize: '11px',
                                                                position: 'relative'
                                                            }, children: [_jsx("button", { onClick: () => handleRemoveStream(item.id), style: {
                                                                        position: 'absolute',
                                                                        top: '4px',
                                                                        right: '4px',
                                                                        background: 'rgba(239, 68, 68, 0.8)',
                                                                        border: 'none',
                                                                        borderRadius: '3px',
                                                                        color: 'white',
                                                                        fontSize: '10px',
                                                                        padding: '2px 6px',
                                                                        cursor: 'pointer',
                                                                        fontWeight: '600'
                                                                    }, children: "\u00C3\u2014" }), _jsxs("div", { style: { fontWeight: '600', marginBottom: '2px', paddingRight: '20px' }, children: ["\u00F0\u0178\u201C\u00A1 ", item.title] }), _jsxs("div", { style: { fontSize: '10px', opacity: 0.6 }, children: [formatDuration(item.duration), " \u00E2\u20AC\u00A2 Live Stream"] })] }, item.id))) }))] }))] })), activeTab === 'queue' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: "Drag to Schedule:" }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: MOCK_QUEUE.map(item => (_jsxs("div", { draggable: true, onDragStart: (e) => handleDragStart(e, item), style: {
                                                        padding: '8px',
                                                        background: 'rgba(255,255,255,0.05)',
                                                        borderRadius: '6px',
                                                        border: '2px solid #ef4444',
                                                        cursor: 'grab',
                                                        fontSize: '11px'
                                                    }, children: [_jsxs("div", { style: { fontWeight: '600', marginBottom: '2px' }, children: ["\u00F0\u0178\u201D\u00B4 ", item.title] }), _jsx("div", { style: { fontSize: '10px', opacity: 0.6 }, children: formatDuration(item.duration) })] }, item.id))) })] })), activeTab === 'broadcast' && (_jsx(_Fragment, { children: _jsxs("div", { style: { marginBottom: '16px' }, children: [_jsx("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: "BROADCAST INTERMISSION SOURCES" }), _jsx("div", { style: { fontSize: '9px', marginBottom: '12px', opacity: 0.5, fontStyle: 'italic' }, children: "Content that plays automatically when the queue is empty" }), _jsxs("div", { style: { marginBottom: '12px' }, children: [_jsx("label", { style: {
                                                                display: 'block',
                                                                fontSize: '10px',
                                                                fontWeight: '600',
                                                                marginBottom: '4px',
                                                                opacity: 0.7
                                                            }, children: "Add YouTube Video:" }), _jsx("input", { type: "text", placeholder: "YouTube URL or Video ID", value: broadcastYoutubeUrl, onChange: (e) => setBroadcastYoutubeUrl(e.target.value), onKeyPress: (e) => e.key === 'Enter' && handleAddBroadcastYouTubeVideo(), style: {
                                                                width: '100%',
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.1)',
                                                                border: '1px solid rgba(255,255,255,0.2)',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                marginBottom: '6px'
                                                            } }), _jsx("button", { onClick: handleAddBroadcastYouTubeVideo, disabled: !broadcastYoutubeUrl.trim(), style: {
                                                                width: '100%',
                                                                padding: '8px',
                                                                background: broadcastYoutubeUrl.trim() ? '#ef4444' : '#666',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                cursor: broadcastYoutubeUrl.trim() ? 'pointer' : 'not-allowed',
                                                                fontWeight: '600'
                                                            }, children: "Add Video" })] }), _jsxs("div", { style: { marginBottom: '16px' }, children: [_jsx("label", { style: {
                                                                display: 'block',
                                                                fontSize: '10px',
                                                                fontWeight: '600',
                                                                marginBottom: '4px',
                                                                opacity: 0.7
                                                            }, children: "Add YouTube Playlist:" }), _jsx("input", { type: "text", placeholder: "YouTube Playlist URL or ID", value: broadcastPlaylistUrl, onChange: (e) => setBroadcastPlaylistUrl(e.target.value), onKeyPress: (e) => e.key === 'Enter' && handleAddBroadcastYouTubePlaylist(), style: {
                                                                width: '100%',
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.1)',
                                                                border: '1px solid rgba(255,255,255,0.2)',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                marginBottom: '6px'
                                                            } }), _jsxs("select", { value: playlistMode, onChange: (e) => setPlaylistMode(e.target.value), style: {
                                                                width: '100%',
                                                                padding: '8px',
                                                                background: 'rgba(255,255,255,0.1)',
                                                                border: '1px solid rgba(255,255,255,0.2)',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                marginBottom: '6px',
                                                                cursor: 'pointer'
                                                            }, children: [_jsx("option", { value: "individual", style: { background: '#1e1b4b' }, children: "Individual Videos" }), _jsx("option", { value: "block", style: { background: '#1e1b4b' }, children: "Playlist Block" })] }), _jsx("div", { style: { fontSize: '9px', marginBottom: '6px', opacity: 0.5, fontStyle: 'italic' }, children: "Individual: Each video plays separately | Block: Entire playlist as one unit" }), _jsx("button", { onClick: handleAddBroadcastYouTubePlaylist, disabled: !broadcastPlaylistUrl.trim(), style: {
                                                                width: '100%',
                                                                padding: '8px',
                                                                background: broadcastPlaylistUrl.trim() ? '#8b5cf6' : '#666',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                cursor: broadcastPlaylistUrl.trim() ? 'pointer' : 'not-allowed',
                                                                fontWeight: '600'
                                                            }, children: "Add Playlist" })] }), _jsxs("div", { style: { marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }, children: [_jsx("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: "CURRENT ROTATION ORDER" }), _jsx("div", { style: {
                                                                background: 'rgba(139, 92, 246, 0.1)',
                                                                borderRadius: '6px',
                                                                padding: '8px',
                                                                border: '1px solid rgba(139, 92, 246, 0.3)',
                                                                maxHeight: '200px',
                                                                overflowY: 'auto'
                                                            }, children: (() => {
                                                                const rotationVideos = [];
                                                                const enabledSources = broadcastSources.filter((s) => s.enabled);
                                                                // Build flat list of videos with source index tracking
                                                                enabledSources.forEach((source, sourceIdx) => {
                                                                    if (source.type === 'youtube') {
                                                                        rotationVideos.push({
                                                                            sourceId: source.id,
                                                                            sourceName: source.title,
                                                                            sourceType: source.type,
                                                                            sourceIdx: sourceIdx, // Track which enabled source this belongs to
                                                                            title: source.title,
                                                                            videoId: source.videoId,
                                                                            duration: source.duration
                                                                        });
                                                                    }
                                                                    else if (source.type === 'youtube-playlist' && source.mode === 'individual' && source.videos) {
                                                                        source.videos.forEach((video) => {
                                                                            rotationVideos.push({
                                                                                sourceId: source.id,
                                                                                sourceName: source.title,
                                                                                sourceType: source.type,
                                                                                sourceIdx: sourceIdx, // All videos from this source have same sourceIdx
                                                                                title: video.title,
                                                                                videoId: video.videoId,
                                                                                duration: video.duration
                                                                            });
                                                                        });
                                                                    }
                                                                    else if (source.type === 'youtube-playlist' && source.mode === 'block') {
                                                                        rotationVideos.push({
                                                                            sourceId: source.id,
                                                                            sourceName: source.title,
                                                                            sourceType: source.type,
                                                                            sourceIdx: sourceIdx,
                                                                            title: `${source.title} (${source.videos?.length || 0} videos)`,
                                                                            isBlock: true,
                                                                            duration: source.videos?.reduce((sum, v) => sum + (v.duration || 0), 0) || 0
                                                                        });
                                                                    }
                                                                    else if (source.type === 'local' && source.videos) {
                                                                        source.videos.forEach((video) => {
                                                                            rotationVideos.push({
                                                                                sourceId: source.id,
                                                                                sourceName: 'Local Files',
                                                                                sourceType: source.type,
                                                                                sourceIdx: sourceIdx,
                                                                                title: video.title || video.filename,
                                                                                filename: video.filename,
                                                                                duration: video.duration
                                                                            });
                                                                        });
                                                                    }
                                                                });
                                                                // Find which video index corresponds to current broadcast index
                                                                // currentBroadcastIndex is the index in enabledSources
                                                                const currentSourceIdx = currentBroadcastIndex % enabledSources.length;
                                                                const currentVideoIdx = rotationVideos.findIndex((v) => v.sourceIdx === currentSourceIdx);
                                                                if (rotationVideos.length === 0) {
                                                                    return (_jsx("div", { style: { textAlign: 'center', padding: '10px', fontSize: '10px', opacity: 0.6 }, children: "No videos in rotation" }));
                                                                }
                                                                return (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '4px' }, children: rotationVideos.map((video, idx) => {
                                                                        const isCurrent = idx === currentVideoIdx;
                                                                        return (_jsx("div", { style: {
                                                                                padding: '6px 8px',
                                                                                background: isCurrent ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255,255,255,0.05)',
                                                                                borderRadius: '4px',
                                                                                border: isCurrent ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                                                                                fontSize: '10px'
                                                                            }, children: _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '6px' }, children: [_jsx("div", { style: { fontWeight: '700', minWidth: '20px', opacity: 0.6 }, children: isCurrent ? 'â–¶' : `${idx + 1}.` }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("div", { style: { fontWeight: '600', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: video.title }), _jsxs("div", { style: { fontSize: '9px', opacity: 0.5 }, children: [video.sourceName, " \u00E2\u20AC\u00A2 ", video.duration ? formatDuration(video.duration) : 'Unknown', isCurrent && ' â€¢ PLAYING NEXT'] })] })] }) }, `rotation-${idx}`));
                                                                    }) }));
                                                            })() })] }), _jsxs("div", { children: [_jsxs("div", { style: { fontSize: '10px', fontWeight: '600', marginBottom: '8px', opacity: 0.7 }, children: ["BROADCAST SOURCES (", broadcastSources.filter((s) => s.enabled).length, " enabled)"] }), broadcastSources.length === 0 ? (_jsxs("div", { style: { textAlign: 'center', padding: '20px', fontSize: '11px', opacity: 0.6 }, children: ["No broadcast sources configured", _jsx("div", { style: { fontSize: '9px', marginTop: '8px', opacity: 0.5 }, children: "Add YouTube videos or playlists above" })] })) : (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: broadcastSources.map((source) => {
                                                                const isExpanded = expandedBroadcastSources.has(source.id);
                                                                const hasVideos = (source.videos && source.videos.length > 0) || source.type === 'youtube';
                                                                return (_jsxs("div", { style: {
                                                                        padding: '10px',
                                                                        background: source.enabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                                                                        borderRadius: '6px',
                                                                        border: `1px solid ${source.enabled ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                                                                        fontSize: '11px',
                                                                        opacity: source.enabled ? 1 : 0.5
                                                                    }, children: [_jsxs("div", { style: {
                                                                                display: 'flex',
                                                                                alignItems: 'flex-start',
                                                                                marginBottom: '6px',
                                                                                cursor: hasVideos ? 'pointer' : 'default'
                                                                            }, onClick: () => hasVideos && toggleBroadcastSourceExpanded(source.id), children: [_jsxs("div", { style: { fontSize: '18px', marginRight: '8px' }, children: [source.type === 'local' && 'ðŸ“', source.type === 'youtube' && 'ðŸ”´', source.type === 'youtube-playlist' && 'ðŸ“‹'] }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: { fontWeight: '600', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: [source.title || source.id, hasVideos && (_jsx("span", { style: { marginLeft: '6px', fontSize: '10px', opacity: 0.6 }, children: isExpanded ? 'â–¼' : 'â–¶' }))] }), source.type === 'youtube-playlist' && (_jsxs("div", { style: { fontSize: '9px', opacity: 0.6 }, children: [source.mode === 'individual' ? 'ðŸ”¢ Individual' : 'ðŸ“¦ Block', " mode \u00E2\u20AC\u00A2 ", source.videos?.length || 0, " videos"] })), source.type === 'local' && (_jsxs("div", { style: { fontSize: '9px', opacity: 0.6 }, children: [source.videos?.length || 0, " files"] })), source.type === 'youtube' && (_jsxs("div", { style: { fontSize: '9px', opacity: 0.6 }, children: ["Single video \u00E2\u20AC\u00A2 ", source.duration ? formatDuration(source.duration) : 'Unknown duration'] }))] })] }), isExpanded && hasVideos && (_jsx("div", { style: {
                                                                                marginBottom: '8px',
                                                                                paddingTop: '8px',
                                                                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto'
                                                                            }, children: source.type === 'youtube' ? (_jsxs("div", { style: {
                                                                                    padding: '6px 8px',
                                                                                    background: 'rgba(255,255,255,0.03)',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '10px'
                                                                                }, children: [_jsx("div", { style: { fontWeight: '500', marginBottom: '2px' }, children: source.title }), _jsxs("div", { style: { opacity: 0.5, fontSize: '9px' }, children: ["Video ID: ", source.videoId] })] })) : source.videos && source.videos.length > 0 ? (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '4px' }, children: source.videos.map((video, idx) => (_jsxs("div", { style: {
                                                                                        padding: '6px 8px',
                                                                                        background: 'rgba(255,255,255,0.03)',
                                                                                        borderRadius: '4px',
                                                                                        fontSize: '10px',
                                                                                        position: 'relative',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '8px'
                                                                                    }, children: [_jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsxs("div", { style: { fontWeight: '500', marginBottom: '2px' }, children: [idx + 1, ". ", video.title || video.filename] }), _jsxs("div", { style: { opacity: 0.5, fontSize: '9px' }, children: [video.duration ? formatDuration(video.duration) : 'Unknown duration', video.videoId && ` â€¢ ${video.videoId}`] })] }), source.type === 'youtube-playlist' && video.videoId && (_jsx("button", { onClick: (e) => {
                                                                                                e.stopPropagation();
                                                                                                handleRemoveVideoFromPlaylist(source.id, video.videoId);
                                                                                            }, style: {
                                                                                                padding: '4px 6px',
                                                                                                background: 'rgba(239, 68, 68, 0.2)',
                                                                                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                                                                                borderRadius: '3px',
                                                                                                color: 'white',
                                                                                                fontSize: '8px',
                                                                                                cursor: 'pointer',
                                                                                                fontWeight: '600',
                                                                                                flexShrink: 0
                                                                                            }, title: "Remove from playlist", children: "\u00E2\u0153\u2022" }))] }, video.videoId || video.filename || idx))) })) : (_jsx("div", { style: { textAlign: 'center', padding: '10px', opacity: 0.5, fontSize: '10px' }, children: "No videos available" })) })), _jsxs("div", { style: { display: 'flex', gap: '4px' }, children: [source.type === 'youtube-playlist' && (_jsx("button", { onClick: () => handleRefreshPlaylistSource(source.id), style: {
                                                                                        flex: 1,
                                                                                        padding: '4px 8px',
                                                                                        background: 'rgba(255,255,255,0.1)',
                                                                                        border: '1px solid rgba(255,255,255,0.2)',
                                                                                        borderRadius: '3px',
                                                                                        color: 'white',
                                                                                        fontSize: '9px',
                                                                                        cursor: 'pointer',
                                                                                        fontWeight: '600'
                                                                                    }, children: "\u00F0\u0178\u201D\u201E Refresh" })), source.type !== 'local' && (_jsxs(_Fragment, { children: [_jsx("button", { onClick: () => handleToggleBroadcastSource(source.id), style: {
                                                                                                flex: 1,
                                                                                                padding: '4px 8px',
                                                                                                background: source.enabled ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255,255,255,0.1)',
                                                                                                border: `1px solid ${source.enabled ? 'rgba(34, 197, 94, 0.4)' : 'rgba(255,255,255,0.2)'}`,
                                                                                                borderRadius: '3px',
                                                                                                color: 'white',
                                                                                                fontSize: '9px',
                                                                                                cursor: 'pointer',
                                                                                                fontWeight: '600'
                                                                                            }, children: source.enabled ? 'âœ“ Enabled' : 'âœ— Disabled' }), _jsx("button", { onClick: () => handleRemoveBroadcastSource(source.id), style: {
                                                                                                flex: 1,
                                                                                                padding: '4px 8px',
                                                                                                background: 'rgba(239, 68, 68, 0.2)',
                                                                                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                                                                                borderRadius: '3px',
                                                                                                color: 'white',
                                                                                                fontSize: '9px',
                                                                                                cursor: 'pointer',
                                                                                                fontWeight: '600'
                                                                                            }, children: "Remove" })] }))] })] }, source.id));
                                                            }) }))] })] }) }))] })] })] }), _jsxs("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }, children: [_jsxs("div", { style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 16px',
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }, children: [_jsx("button", { onClick: previousMonth, style: {
                                    padding: '6px 12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '6px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }, children: "\u00E2\u2014\u20AC Prev" }), _jsx("h2", { style: { margin: 0, fontSize: '18px', fontWeight: '700' }, children: monthYearText }), _jsx("button", { onClick: nextMonth, style: {
                                    padding: '6px 12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '6px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }, children: "Next \u00E2\u2013\u00B6" })] }), _jsxs("div", { style: {
                            flex: 1,
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px',
                            padding: '12px',
                            overflowY: 'auto',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column'
                        }, children: [_jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '6px' }, children: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (_jsx("div", { style: { textAlign: 'center', fontSize: '11px', fontWeight: '700', opacity: 0.7 }, children: day }, day))) }), _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', flex: 1 }, children: days.map((date, index) => (_jsx("div", { style: {
                                        background: date ? 'rgba(255,255,255,0.05)' : 'transparent',
                                        borderRadius: '6px',
                                        padding: date ? '8px' : '0',
                                        border: date ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }, children: date && (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                                    fontSize: '13px',
                                                    fontWeight: '700',
                                                    marginBottom: '6px',
                                                    color: date.toDateString() === new Date().toDateString() ? '#10b981' : 'rgba(255,255,255,0.9)'
                                                }, children: date.getDate() }), _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '2px' }, children: Array.from({ length: 24 }, (_, hour) => {
                                                    const eventsInHour = getScheduledContentForHour(date, hour);
                                                    const slotKey = `${date.toDateString()}-${hour}`;
                                                    const isHovered = hoveredSlot === slotKey;
                                                    const isExpanded = expandedSlot?.key === slotKey;
                                                    const startingEvent = eventsInHour.find(event => {
                                                        const hourSpan = getEventHourSpan(event);
                                                        const thisHour = hourSpan.find(h => h.date === date.toDateString() && h.hour === hour);
                                                        return thisHour?.isStart;
                                                    });
                                                    const timeLabel = hour === 0 ? '12a' : hour < 12 ? `${hour}a` : hour === 12 ? '12p' : `${hour - 12}p`;
                                                    return (_jsxs(React.Fragment, { children: [_jsxs("div", { onClick: (e) => handleSlotClick(date, hour, e), onDragOver: (e) => handleDragOver(e, date, hour), onDrop: (e) => handleDrop(e, date, hour), onMouseEnter: () => setHoveredSlot(slotKey), onMouseLeave: () => setHoveredSlot(null), style: {
                                                                    width: '100%',
                                                                    height: '30px',
                                                                    background: 'rgba(255,255,255,0.15)',
                                                                    borderRadius: '2px',
                                                                    cursor: 'pointer',
                                                                    border: isHovered
                                                                        ? '2px solid rgba(255,255,255,0.9)'
                                                                        : isExpanded
                                                                            ? '2px solid #8b5cf6'
                                                                            : '1px solid rgba(255,255,255,0.25)',
                                                                    position: 'relative',
                                                                    transition: 'all 0.15s',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    overflow: 'hidden'
                                                                }, children: [_jsx("span", { style: {
                                                                            fontSize: '10px',
                                                                            fontWeight: '700',
                                                                            color: 'rgba(255,255,255,0.95)',
                                                                            pointerEvents: 'none',
                                                                            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                                                            position: 'relative',
                                                                            zIndex: 2
                                                                        }, children: timeLabel }), eventsInHour.map(event => {
                                                                        const fillPercent = getHourFillPercentage(date, hour, event);
                                                                        const eventStart = new Date(event.startTime);
                                                                        const hourStart = new Date(date);
                                                                        hourStart.setHours(hour, 0, 0, 0);
                                                                        const offsetMinutes = Math.max(0, (eventStart - hourStart) / 60000);
                                                                        const offsetPercent = (offsetMinutes / 60) * 100;
                                                                        const eventColor = getEventColor(event, scheduledEvents);
                                                                        return (_jsx("div", { onMouseEnter: () => setHoveredEvent(event.id), onMouseLeave: () => setHoveredEvent(null), style: {
                                                                                position: 'absolute',
                                                                                left: `${offsetPercent}%`,
                                                                                top: 0,
                                                                                width: `${fillPercent}%`,
                                                                                height: '100%',
                                                                                background: eventColor,
                                                                                opacity: 0.9,
                                                                                zIndex: 1
                                                                            }, title: event.title, children: renderShiftIndicator(event.id) }, event.id));
                                                                    }), (() => {
                                                                        const broadcastFillPercent = getBroadcastFillPercentage(date, hour, eventsInHour);
                                                                        if (broadcastFillPercent > 0) {
                                                                            return (_jsx("div", { style: {
                                                                                    position: 'absolute',
                                                                                    right: 0,
                                                                                    top: 0,
                                                                                    width: `${broadcastFillPercent}%`,
                                                                                    height: '100%',
                                                                                    background: '#eab308',
                                                                                    opacity: 0.5,
                                                                                    zIndex: 0
                                                                                } }));
                                                                        }
                                                                        return null;
                                                                    })(), ghostPreview && (() => {
                                                                        const ghostStart = new Date(ghostPreview.startTime);
                                                                        if (ghostStart.toDateString() === date.toDateString() &&
                                                                            ghostStart.getHours() === hour) {
                                                                            const fillPercent = getHourFillPercentage(date, hour, ghostPreview);
                                                                            return (_jsx("div", { style: {
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    width: `${fillPercent}%`,
                                                                                    height: '100%',
                                                                                    background: 'rgba(139, 92, 246, 0.4)',
                                                                                    border: '2px dashed rgba(139, 92, 246, 0.8)',
                                                                                    borderRadius: '2px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    fontSize: '9px',
                                                                                    color: 'white',
                                                                                    pointerEvents: 'none',
                                                                                    zIndex: 1000,
                                                                                    animation: 'ghostPulse 1s ease-in-out infinite'
                                                                                }, children: "\u00F0\u0178\u2018\u00BB" }));
                                                                        }
                                                                        return null;
                                                                    })(), startingEvent && (_jsx("div", { draggable: true, onDragStart: (e) => handleEventDragStart(e, startingEvent), onClick: (e) => handleRemoveEvent(startingEvent.id, e), style: {
                                                                            position: 'absolute',
                                                                            top: '-2px',
                                                                            left: '-2px',
                                                                            width: '8px',
                                                                            height: '8px',
                                                                            background: 'white',
                                                                            borderRadius: '50%',
                                                                            cursor: 'move',
                                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                                            zIndex: 3
                                                                        }, title: "Drag to move or click to remove" }))] }), isExpanded && renderExpandedHourView(date, hour)] }, hour));
                                                }) })] })) }, index))) })] }), _jsxs("div", { style: {
                            display: 'flex',
                            gap: '12px',
                            padding: '8px 12px',
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            fontSize: '10px'
                        }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx("div", { style: { width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' } }), _jsx("span", { children: "Red" })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx("div", { style: { width: '12px', height: '12px', background: '#f97316', borderRadius: '2px' } }), _jsx("span", { children: "Orange" })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx("div", { style: { width: '12px', height: '12px', background: '#f59e0b', borderRadius: '2px' } }), _jsx("span", { children: "Amber" })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx("div", { style: { width: '12px', height: '12px', background: '#10b981', borderRadius: '2px' } }), _jsx("span", { children: "Green" })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx("div", { style: { width: '12px', height: '12px', background: '#eab308', borderRadius: '2px' } }), _jsx("span", { children: "Broadcast" })] }), _jsx("div", { style: { marginLeft: 'auto', opacity: 0.5 }, children: "12 colors rotate \u00E2\u20AC\u00A2 Click to expand \u00E2\u20AC\u00A2 Drag to move" })] })] })] }));
};
export default CalendarAdminPanel;
